import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styled from 'styled-components'

import { PlusOutlined, CaretRightOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Collapse,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Row,
  Select,
  Typography
} from 'antd'

import { STUDENT } from '../../constants/userRoles'

import {
  createCourse,
  getAllCourses,
  deleteCourse,
  unEnroll
} from '../../reducers/courseReducer'
import Spinner from '../../components/Spinner'
import CourseCard from '../../components/CourseCard'
import { useHistory } from 'react-router'
import { getAllDeadlines } from '../../reducers/deadlinesReducer'
import { DateTime } from 'luxon'

import { FileTextOutlined } from '@ant-design/icons'
import { AiOutlineSolution } from 'react-icons/ai'

const DashboardHero = styled.div`
  background: linear-gradient(
    135deg,
    hsl(210, 55%, 22%) 0%,
    hsl(210, 60%, 15%) 100%
  );
  border-radius: 16px;
  padding: 40px;
  color: white;
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);

  .text-content h1 {
    font-size: 2.2rem;
    font-weight: 700;
    color: white;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
  }
  .text-content p {
    font-size: 1.1rem;
    color: #cbd5e1;
    margin: 0;
  }

  .action-btn {
    background: hsl(33, 100%, 50%);
    border-color: hsl(33, 100%, 50%);
    color: white;
    font-weight: 600;
    height: 48px;
    padding: 0 24px;
    border-radius: 8px;
    font-size: 1.05rem;
  }
  .action-btn:hover {
    background: hsl(33, 100%, 45%);
    border-color: hsl(33, 100%, 45%);
    color: white;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 24px;
    padding: 30px 20px;
  }
`

const { Title, Text } = Typography

const Dashboard = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  useEffect(() => {
    dispatch(getAllCourses())
    dispatch(getAllDeadlines())
  }, [dispatch])

  const user = useSelector((state) => state.auth.user)
  const courses = useSelector((state) =>
    state.courses.data.filter((course) => course.status !== 'archived')
  )
  const loading = useSelector((state) => state.courses.loading)
  const deadlines = useSelector((state) => state.deadlines.data)
  const deadlinesLoading = useSelector((state) => state.deadlines.loading)

  const [form] = Form.useForm()
  const [modalVisible, setModalVisible] = useState(false)

  const enrolledCourses = courses.filter((course) => course.enrolled)

  const handleCancel = () => {
    setModalVisible(false)
  }

  const addCourse = (course) => {
    dispatch(createCourse(course))
  }

  const removeCourse = (courseId) => {
    dispatch(deleteCourse(courseId))
  }

  const handleUnenroll = (courseId, userId) => {
    dispatch(unEnroll(courseId, userId))
  }

  const handleCourseCardClick = (courseId) => {
    history.push(`/app/course/${courseId}/modules`)
  }

  if (loading) return <Spinner size="large" />

  return (
    <>
      <DashboardHero>
        <div className="text-content">
          <h1>Welcome Back, {user?.name || 'Learner'}! 👋</h1>
          <p>
            Track your progress, view upcoming deadlines, and manage your career
            path.
          </p>
        </div>
        {user && user.role !== STUDENT && (
          <Button
            className="action-btn"
            onClick={() => setModalVisible(true)}
            icon={<PlusOutlined />}
          >
            Create Course
          </Button>
        )}
      </DashboardHero>

      <Modal
        title="Add New Course"
        visible={modalVisible}
        onOk={form.submit}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={form.submit}>
            Submit
          </Button>
        ]}
      >
        <Form
          name="add Course"
          form={form}
          onFinish={addCourse}
          requiredMark={false}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
        >
          <Form.Item
            name="courseName"
            label="Course Name"
            rules={[
              {
                required: true,
                message: 'Please enter the course name'
              }
            ]}
          >
            <Input placeholder="Course Name" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea placeholder="(Optional)" allowClear />
          </Form.Item>

          <Form.Item name="image" label="Cover Image">
            <Input placeholder="(Optional) Image Url, defaults to random colour" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select or enter a category' }]}
          >
            <Select
              mode="tags"
              maxCount={1}
              placeholder="Select or type a category (e.g., IT, Business)"
              style={{ width: '100%' }}
            >
              <Select.Option value="IT">IT</Select.Option>
              <Select.Option value="Health">Health</Select.Option>
              <Select.Option value="Business">Business</Select.Option>
              <Select.Option value="Management">Management</Select.Option>
              <Select.Option value="Personal">Personal Dev</Select.Option>
              <Select.Option value="Marketing">Marketing</Select.Option>
              <Select.Option value="Engineering">Engineering</Select.Option>
              <Select.Option value="Teaching">Teaching</Select.Option>
              <Select.Option value="Language">Language</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            initialValue="Certificate"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select placeholder="Course Type">
              <Select.Option value="Diploma">Diploma</Select.Option>
              <Select.Option value="Certificate">Certificate</Select.Option>
              <Select.Option value="Skill Path">Skill Path</Select.Option>
              <Select.Option value="Corporate Training">Corporate Training</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Row gutter={[16, 16]} style={{ marginLeft: 0, marginRight: 0 }}>
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={16}
          xl={19}
          style={{ paddingLeft: 0, paddingRight: 0 }}
        >
          <div style={{ marginTop: 8 }}>
            <Collapse
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
              defaultActiveKey={['1']}
              ghost
            >
              <Collapse.Panel header={<Text strong>My Courses</Text>} key="1">
                <List
                  grid={{
                    gutter: 16,
                    xs: 1,
                    sm: 1,
                    md: 2,
                    lg: 2,
                    xl: 2,
                    xxl: 4
                  }}
                  dataSource={enrolledCourses}
                  renderItem={(course) => (
                    <List.Item>
                      <CourseCard
                        course={course}
                        removeCourse={() => removeCourse(course.id)}
                        handleUnenroll={() =>
                          handleUnenroll(course.id, user._id)
                        }
                        onClick={() => handleCourseCardClick(course.id)}
                      />
                    </List.Item>
                  )}
                />
              </Collapse.Panel>
            </Collapse>
          </div>
        </Col>

        <Col
          xs={24}
          sm={24}
          md={24}
          lg={8}
          xl={5}
          style={{ paddingLeft: 0, paddingRight: 0 }}
        >
          <DeadlinesViewer loading={deadlinesLoading} deadlines={deadlines} />
        </Col>
      </Row>
    </>
  )
}

const DeadLinesContainer = styled.div`
  height: auto;
  width: 100%;
  margin-top: 16px;
  @media (min-width: 768px) {
    margin-top: 0;
  }
`

const DeadlineItem = styled(List.Item)`
  background-color: hsl(33, 100%, 97%);
  border: 1px solid hsl(33, 100%, 90%);
  padding: 16px !important;
  margin-bottom: 8px;
  border-radius: 8px;
  flex-wrap: wrap;
  @media (min-width: 768px) {
    padding: 20px !important;
  }
  .ant-list-item-meta-content {
    min-width: 0;
  }
  .ant-list-item-meta-title {
    word-break: break-word;
  }
  .ant-typography {
    flex-shrink: 0;
  }
`

const DeadlinesViewer = (props) => {
  const { loading, deadlines } = props

  if (loading) return <Spinner size="large" />

  return (
    <DeadLinesContainer>
      <div
        style={{
          height: '70px',
          backgroundImage: `linear-gradient(hsl(33, 100%, 50%), hsl(33, 100%, 35%))`,
          color: 'white',
          padding: '20px',
          paddingRight: '0px'
        }}
      >
        <Typography.Title style={{ color: 'white' }} level={4}>
          Upcoming Deadlines
        </Typography.Title>
      </div>
      <List
        style={{
          maxHeight: 'min(445px, 50vh)',
          overflow: 'hidden',
          overflowY: 'auto'
        }}
        dataSource={deadlines.filter((item) => {
          return DateTime.fromISO(item.deadline) >= DateTime.now()
        })}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={'No Deadlines'}
            />
          )
        }}
        renderItem={(item) => {
          const date = DateTime.fromISO(item.deadline)
          return (
            <DeadlineItem
              style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}
            >
              <List.Item.Meta
                avatar={
                  item.type === 'Exam' ? (
                    <AiOutlineSolution style={{ fontSize: 16 }} />
                  ) : (
                    <FileTextOutlined />
                  )
                }
                title={
                  <a
                    href={`/app/course/${item.course.id}/${item.type.toLowerCase()}/${item.assessmentId}`}
                  >
                    {item.title}
                  </a>
                }
                description={item.course.name}
              />
              <Text
                type="secondary"
                style={{ flexShrink: 0, fontSize: '12px' }}
              >
                {date.toLocaleString(DateTime.DATETIME_MED)}
              </Text>
            </DeadlineItem>
          )
        }}
      />
    </DeadLinesContainer>
  )
}

export default Dashboard
